/**
 * Platform admin callables (AGENTS.md section 19). The admin app never reads
 * Firestore; every screen is one of these payloads. Nothing here carries
 * patient data (D21): clinic rows are counts and configuration only.
 */
import { z } from "zod";
import { emailSchema } from "../common.js";
export const PLAN_KEYS = ["starter", "clinic", "group"];
export const planKeySchema = z.enum(PLAN_KEYS);
export const ADMIN_CLINIC_FILTERS = [
    "all",
    "trial",
    "paying",
    "comp",
    "expired",
    "at_risk",
];
export const adminClinicFilterSchema = z.enum(ADMIN_CLINIC_FILTERS);
export const adminListClinicsRequestSchema = z.object({
    filter: adminClinicFilterSchema.default("all"),
    search: z.string().max(120).optional(),
    limit: z.number().int().min(1).max(500).default(200),
});
export const adminGrantLicenceRequestSchema = z
    .object({
    clinicId: z.string().min(1).max(64).optional(),
    email: emailSchema.optional(),
    plan: planKeySchema,
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().nullable().optional(),
    note: z.string().max(500).optional(),
    /** Websites covered (section 13); default 1. */
    sites: z.number().int().min(1).max(100).optional(),
    /** `manual`: paid by bank transfer, invoiced by hand (owner request, 14 October 2026). */
    source: z.enum(["comp", "manual"]).optional(),
})
    .refine((v) => Boolean(v.clinicId) !== Boolean(v.email), {
    message: "exactly one of clinicId or email",
});
/** Edit a clinic's licence by hand: dates, kind, plan, websites, note (owner request, 14 October 2026). */
export const adminUpdateLicenceRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    source: z.enum(["stripe", "comp", "trial", "manual"]),
    plan: planKeySchema,
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime().nullable(),
    sites: z.number().int().min(1).max(100),
    note: z.string().max(500).optional(),
    reason: z.string().max(500).optional(),
});
export const adminPendingLicenceIdSchema = z.object({ id: z.string().regex(/^[a-f0-9]{64}$/) });
export const adminUpdatePendingLicenceRequestSchema = z.object({
    id: z.string().regex(/^[a-f0-9]{64}$/),
    source: z.enum(["comp", "manual"]),
    plan: planKeySchema,
    startsAt: z.string().datetime().nullable(),
    endsAt: z.string().datetime().nullable(),
    sites: z.number().int().min(1).max(100),
    note: z.string().max(500).optional(),
});
/** Permanently deletes a clinic and everything under it (owner request, 14 October 2026). */
export const adminDeleteClinicRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    /** Must be the word DELETE, typed by the person. */
    confirm: z.literal("DELETE"),
    reason: z.string().min(2).max(500),
    /** Required when the clinic has leads; the admin app asks a second time. */
    withLeads: z.boolean().optional(),
});
export const adminRevokeLicenceRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    reason: z.string().min(2).max(500),
});
export const adminExtendTrialRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    endsAt: z.string().datetime(),
    note: z.string().max(500).optional(),
});
export const adminImpersonateRequestSchema = z.object({ clinicId: z.string().min(1).max(64) });
export const adminSetOverridesRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    limits: z
        .object({
        leadsPerMonth: z.number().int().positive().nullable().optional(),
        forms: z.number().int().positive().nullable().optional(),
        members: z.number().int().positive().nullable().optional(),
        doctors: z.number().int().positive().nullable().optional(),
        sites: z.number().int().positive().nullable().optional(),
    })
        .optional(),
    flags: z.record(z.string().min(1).max(40), z.boolean()).optional(),
});
export const adminSetSupportNoteRequestSchema = z.object({
    clinicId: z.string().min(1).max(64),
    note: z.string().max(4000),
});
export const adminEmailLogRequestSchema = z.object({
    clinicId: z.string().min(1).max(64).optional(),
    limit: z.number().int().min(1).max(200).default(100),
    before: z.string().datetime().optional(),
});
export const adminAuditLogRequestSchema = z.object({
    clinicId: z.string().min(1).max(64).optional(),
    actorUid: z.string().min(1).max(128).optional(),
    limit: z.number().int().min(1).max(200).default(100),
    before: z.string().datetime().optional(),
});
export const adminSetPlatformUserRequestSchema = z.object({
    email: emailSchema,
    platformRole: z.enum(["superadmin", "support"]).nullable(),
});
/** Called from the clinic dashboard during a support session (D21). */
export const revealPatientDataRequestSchema = z.object({
    reason: z.string().min(5).max(500),
    leadRef: z.string().max(12).optional(),
});
//# sourceMappingURL=admin.js.map